import { AlertTriangle, ArrowRight } from "lucide-react";
import { Tag } from "@/shared/components/ui/tag";
import { useIntercom } from "@/shared/providers/IntercomProvider";
import {
  InsightsCard,
  InsightsListItem,
  InsightsEmptyState,
  InsightsStat,
  InsightsDividerList,
  InsightsDividerRow,
  CreditScoreBar,
  formatCurrency,
  formatDate,
  formatMonthYear,
} from "./InsightsCard";
import { useCitation, getCitationForField } from "./CitationContext";

// Credit Report extraction data types
export interface CreditReportExtraction {
  report_metadata?: {
    report_date?: string;
    bureau?: string;
    report_number?: string;
  };
  credit_scores?: Array<{
    score: number;
    source: string;
    score_date?: string;
    score_model?: string;
    label?: string;
  }>;
  accounts?: Array<{
    creditor_name: string;
    account_number_last4?: string;
    account_type: string;
    status: string;
    balance: number;
    credit_limit?: number;
    monthly_payment?: number;
    open_date?: string;
    last_payment_date?: string;
    last_reported_date?: string;
    responsibility?: string;
    is_joint_account?: boolean;
    is_authorized_user?: boolean;
    payment_history?: string;
    original_amount?: number;
  }>;
  inquiries?: Array<{
    inquiry_date: string;
    inquirer_name: string;
    inquiry_type?: string;
  }>;
  public_records?: Array<{
    record_type: string;
    filing_date: string;
    amount?: number;
    status: string;
    court_or_jurisdiction?: string;
    discharge_date?: string;
    chapter?: string;
    case_number?: string;
  }>;
  fraud_detection?: {
    has_fraud: boolean;
    date_of_death?: string;
    fraudulent_accounts: Array<{
      creditor_name: string;
      account_number_last4?: string;
      open_date: string;
      balance: number;
      account_type: string;
    }>;
    fraudulent_inquiries: Array<{
      inquirer_name: string;
      inquiry_date: string;
      inquiry_type?: string;
    }>;
    total_fraud_count: number;
  };
  estate_liability_summary?: {
    total_outstanding_debt: number;
    credit_cards: { sum: number; count: number };
    mortgages: { sum: number; count: number };
    auto_loans: { sum: number; count: number };
    student_loans: { sum: number; count: number };
    personal_loans: { sum: number; count: number };
    collections: { sum: number; count: number };
    other: { sum: number; count: number };
  };
  personal_information?: {
    full_name?: string;
    current_address?: string;
    previous_addresses?: string[];
    employers?: string[];
  };
}

export default function CreditReportInsights({
  creditReportData,
  citations,
}: {
  creditReportData: CreditReportExtraction;
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();
  const { openSidePanel } = useIntercom();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const { credit_scores, accounts, public_records, fraud_detection, estate_liability_summary } =
    creditReportData;

  const openAccounts = accounts?.filter((acc) => acc.status === "open") || [];
  const closedAccounts =
    accounts?.filter((acc) => ["closed", "paid", "paid_closed"].includes(acc.status)) || [];

  return (
    <div className="px-6 pb-6 space-y-6">
      {/* Fraud Alert */}
      {fraud_detection?.has_fraud && (
        <InsightsCard
          title="Potential Fraud Detected"
          subtitle={`${fraud_detection.fraudulent_accounts?.length > 0 ? `${fraud_detection.fraudulent_accounts.length} account${fraud_detection.fraudulent_accounts.length !== 1 ? "s" : ""} opened` : ""}${fraud_detection.fraudulent_accounts?.length > 0 && fraud_detection.fraudulent_inquiries?.length > 0 ? " and " : ""}${fraud_detection.fraudulent_inquiries?.length > 0 ? `${fraud_detection.fraudulent_inquiries.length} inquir${fraud_detection.fraudulent_inquiries.length !== 1 ? "ies" : "y"} made` : ""} after date of death${fraud_detection.date_of_death ? ` (${formatDate(fraud_detection.date_of_death)})` : ""}`}
          headerVariant="alert"
          headerContent={<AlertTriangle className="w-4 h-4 text-red-600" />}
        >
          {fraud_detection.fraudulent_accounts?.map((account, idx) => (
            <div key={`account-${idx}`} className="border border-stone-200 rounded-lg p-3 mb-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-semibold text-stone-900">
                    {account.creditor_name}
                    {account.account_number_last4 && ` (...${account.account_number_last4})`}
                  </p>
                  <p className="text-sm text-stone-500 mt-0.5">
                    {account.account_type && `${account.account_type} - `}
                    Opened {formatDate(account.open_date)}
                  </p>
                </div>
                <span className="text-sm font-semibold text-stone-900">
                  {formatCurrency(account.balance)}
                </span>
              </div>
            </div>
          ))}
          {fraud_detection.fraudulent_inquiries?.map((inquiry, idx) => (
            <div key={`inquiry-${idx}`} className="border border-stone-200 rounded-lg p-3 mb-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-semibold text-stone-900">{inquiry.inquirer_name}</p>
                  <p className="text-sm text-stone-500 mt-0.5">
                    Inquiry on {formatDate(inquiry.inquiry_date)}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={openSidePanel}
            className="text-sm text-red-600 font-medium hover:text-red-700 flex items-center gap-1 mt-1"
          >
            Contact support <ArrowRight className="w-4 h-4" />
          </button>
        </InsightsCard>
      )}

      {/* Estate Liability Summary */}
      {estate_liability_summary && (
        <InsightsCard title="Estate Liability Summary" noPadding>
          <div className="p-4">
            <InsightsStat
              label="Total outstanding debt"
              value={formatCurrency(estate_liability_summary.total_outstanding_debt)}
              size="large"
            />
          </div>
          <InsightsDividerList>
            {estate_liability_summary.credit_cards.count > 0 && (
              <InsightsDividerRow
                label={`Credit Cards (${estate_liability_summary.credit_cards.count})`}
                value={formatCurrency(estate_liability_summary.credit_cards.sum)}
                layout="between"
              />
            )}
            {estate_liability_summary.mortgages.count > 0 && (
              <InsightsDividerRow
                label={`Mortgage (${estate_liability_summary.mortgages.count})`}
                value={formatCurrency(estate_liability_summary.mortgages.sum)}
                layout="between"
              />
            )}
            {estate_liability_summary.auto_loans.count > 0 && (
              <InsightsDividerRow
                label={`Auto loan (${estate_liability_summary.auto_loans.count})`}
                value={formatCurrency(estate_liability_summary.auto_loans.sum)}
                layout="between"
              />
            )}
            {estate_liability_summary.student_loans.count > 0 && (
              <InsightsDividerRow
                label={`Student Loans (${estate_liability_summary.student_loans.count})`}
                value={formatCurrency(estate_liability_summary.student_loans.sum)}
                layout="between"
              />
            )}
            {estate_liability_summary.personal_loans.count > 0 && (
              <InsightsDividerRow
                label={`Personal Loans (${estate_liability_summary.personal_loans.count})`}
                value={formatCurrency(estate_liability_summary.personal_loans.sum)}
                layout="between"
              />
            )}
            {estate_liability_summary.collections.count > 0 && (
              <InsightsDividerRow
                label={`Collections (${estate_liability_summary.collections.count})`}
                value={formatCurrency(estate_liability_summary.collections.sum)}
                layout="between"
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {/* Accounts to Close */}
      <InsightsCard title="Accounts to close" noPadding>
        {openAccounts.length > 0 ? (
          <>
            {openAccounts.map((account) => {
              const origIndex = accounts?.indexOf(account) ?? -1;
              return (
                <InsightsListItem
                  key={origIndex}
                  title={account.creditor_name}
                  subtitle={`${account.account_number_last4 ? `-${account.account_number_last4}` : ""}${account.account_number_last4 && account.open_date ? " - " : ""}${account.open_date ? `Opened ${formatMonthYear(account.open_date)}` : ""}`}
                  rightContent={
                    <span className="text-sm font-semibold text-stone-900">
                      {formatCurrency(account.balance)}
                    </span>
                  }
                  badge={<Tag color="green">Open</Tag>}
                  onClick={
                    getCitationForField(citations, `accounts.${origIndex}.creditor_name`)
                      ? () => handleFieldClick(`accounts.${origIndex}.creditor_name`)
                      : undefined
                  }
                />
              );
            })}
          </>
        ) : (
          <div className="p-4">
            <InsightsEmptyState message="All accounts closed" />
          </div>
        )}
      </InsightsCard>

      {/* Closed Accounts */}
      {closedAccounts.length > 0 && (
        <InsightsCard title="Closed accounts" subtitle="No action needed" noPadding>
          <InsightsDividerList>
            {closedAccounts.map((account) => {
              const origIndex = accounts?.indexOf(account) ?? -1;
              return (
                <InsightsDividerRow
                  key={origIndex}
                  label={`${account.creditor_name}${account.account_number_last4 ? ` -${account.account_number_last4}` : ""}`}
                  value={account.status === "paid_closed" ? "Paid" : account.status}
                  onClick={
                    getCitationForField(citations, `accounts.${origIndex}.creditor_name`)
                      ? () => handleFieldClick(`accounts.${origIndex}.creditor_name`)
                      : undefined
                  }
                />
              );
            })}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {/* Credit Score */}
      {(() => {
        const validScores = credit_scores?.filter((s) => s.score && s.score > 0) || [];
        if (validScores.length === 0) return null;

        const ficoScore = validScores.find(
          (s) =>
            s.score_model?.toLowerCase().includes("fico") ||
            s.source?.toLowerCase().includes("fico"),
        );
        const firstScore = ficoScore || validScores[0];

        return (
          <InsightsCard title="Credit Score">
            <CreditScoreBar score={firstScore.score} />
          </InsightsCard>
        );
      })()}

      {/* Public Records */}
      <InsightsCard title="Public Records">
        {public_records && public_records.length > 0 ? (
          <div className="space-y-3">
            {public_records.map((record, idx) => {
              const hasCitation = !!getCitationForField(
                citations,
                `public_records.${idx}.record_type`,
              );
              return (
                <div
                  key={idx}
                  className={`py-2 border-b border-stone-100 last:border-0${hasCitation ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
                  onClick={
                    hasCitation
                      ? () => handleFieldClick(`public_records.${idx}.record_type`)
                      : undefined
                  }
                >
                  <p className="text-sm font-semibold text-stone-900 capitalize inline-flex items-center">
                    {record.record_type.replace(/_/g, " ")}
                    {record.chapter && ` - Chapter ${record.chapter}`}
                    {hasCitation && (
                      <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                    )}
                  </p>
                  {record.amount && (
                    <p className="text-sm text-stone-600">{formatCurrency(record.amount)}</p>
                  )}
                  <p className="text-sm text-stone-500">Filed: {formatDate(record.filing_date)}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <InsightsEmptyState message="No bankruptcies, liens, or judgments found" />
        )}
      </InsightsCard>
    </div>
  );
}
